import type { ConferenceApiStats } from "../conferenceApi/ConferenceApi";
import {
  errorLogSummary,
  screenTrackSettingsSummary,
  trackLogSummary
} from "./sanitizeLogArgs";

// YOUM-253: evidence for "the shared screen is black for viewers".
// This does not fix anything. It turns the sender statistics already collected
// by ConferenceApi into a few bounded, sanitized log events:
// - track transitions (mute / unmute / ended / enabled flips),
// - a capture-stall suspicion with hysteresis (one log per transition),
// - publish lifecycle,
// - a low-rate periodic summary.
// YOUM-268 adds the likely stall cause (capture, encoder or transport) and a
// bounded snapshot that Report Problem sends. Pixels are never read: healthy
// counters do not prove that the shared content is visible.
// Nothing here may throw into the caller: every entry point is guarded.

export const SCREEN_SHARE_EVENTS = {
  publishStart: "screen-share.publish.start",
  publishSuccess: "screen-share.publish.success",
  publishFailure: "screen-share.publish.failure",
  publishStop: "screen-share.publish.stop",
  trackAttached: "screen-share.track.attached",
  trackMute: "screen-share.track.mute",
  trackUnmute: "screen-share.track.unmute",
  trackEnded: "screen-share.track.ended",
  trackEnabledChanged: "screen-share.track.enabled-changed",
  trackEventsSuppressed: "screen-share.track.events-suppressed",
  stallSuspected: "screen-share.stall.suspected",
  stallCleared: "screen-share.stall.cleared",
  summary: "screen-share.sender.summary"
} as const;

export interface ScreenShareDiagnosticsOptions {
  // Consecutive stalled samples before a stall is reported.
  stallEnterSamples: number;
  // Consecutive healthy samples before a reported stall is cleared.
  stallClearSamples: number;
  // A summary is logged on the first assessed sample, then every N samples.
  summaryEverySamples: number;
  // Cap on mute/unmute/enabled logs per stream; counters keep counting.
  maxTrackEventLogs: number;
  // Report Problem snapshot: last N logged events and last N samples.
  historyEvents: number;
  historySamples: number;
}

export const SCREEN_SHARE_DIAGNOSTICS_DEFAULTS: ScreenShareDiagnosticsOptions =
  {
    stallEnterSamples: 3,
    stallClearSamples: 2,
    summaryEverySamples: 8,
    maxTrackEventLogs: 20,
    historyEvents: 30,
    historySamples: 8
  };

// Where frames stop, from the counters alone:
// - capture: the media source stopped delivering frames,
// - encoder: the source delivers frames, nothing is encoded, bytes still flow,
// - capture-or-encoder: nothing is encoded and the browser has no source counter,
// - transport: frames are encoded but nothing leaves the sender, or nothing is
//   encoded nor sent while WebRTC reports a bandwidth limitation,
// - encoder-or-transport: the source delivers frames, nothing is encoded, and
//   the bytes do not show the transport is fine (WebRTC also pauses the
//   encoder when the media path is blocked),
// - unknown: something stopped but the browser lacks the counters to say what.
export type ScreenShareStallCause =
  | "capture"
  | "encoder"
  | "capture-or-encoder"
  | "transport"
  | "encoder-or-transport"
  | "unknown";

// What a Report Problem can tell about one screen share.
export type ScreenShareAssessment =
  | "track-ended-or-muted"
  | "capture-or-encoder-stall-suspected"
  | "transport-stall-suspected"
  | "stall-suspected-cause-unclear"
  | "counters-healthy-content-unverified"
  | "insufficient-evidence";

// Aggregated outbound video counters of one stats sample.
export interface SenderVideoSample {
  ts: number;
  outboundCount: number;
  framesEncoded?: number;
  framesSent?: number;
  bytesSent?: number;
  packetsSent?: number;
  frameWidth?: number;
  frameHeight?: number;
  framesPerSecond?: number;
  qualityLimitationReason?: string;
  sourceFrames?: number;
  sourceFramesPerSecond?: number;
  sourceWidth?: number;
  sourceHeight?: number;
  // What the SFU reports back over RTCP for this video (remote-inbound-rtp).
  remoteRoundTripTime?: number;
  remoteFractionLost?: number;
}

type Counter =
  | "framesEncoded"
  | "framesSent"
  | "bytesSent"
  | "packetsSent"
  | "sourceFrames";
type Deltas = Partial<Record<Counter, number>> & { intervalMs: number };

const COUNTERS: Counter[] = [
  "framesEncoded",
  "framesSent",
  "bytesSent",
  "packetsSent",
  "sourceFrames"
];

// A missing counter (unsupported by the browser) neither proves nor disproves
// progress, so it is ignored rather than read as zero.
export function stallCause(
  deltas: Deltas,
  qualityLimitationReason?: string
): ScreenShareStallCause | null {
  const stopped = (_: number | undefined) => _ !== undefined && _ <= 0;
  const moving = (_: number | undefined) => _ !== undefined && _ > 0;
  const bytesStopped = stopped(deltas.bytesSent) || stopped(deltas.packetsSent);

  if (stopped(deltas.framesEncoded)) {
    if (stopped(deltas.sourceFrames)) {
      return "capture";
    }
    // framesSent cannot move without encoded frames, so only bytes count here.
    // "bandwidth" is common on slow links, so it only names transport when
    // nothing is sent either.
    const bandwidth = qualityLimitationReason === "bandwidth";
    if (bytesStopped && bandwidth) {
      return "transport";
    }
    if (!moving(deltas.sourceFrames)) {
      return "capture-or-encoder";
    }
    const bytesMoving = moving(deltas.bytesSent) || moving(deltas.packetsSent);
    return bytesMoving && !bandwidth ? "encoder" : "encoder-or-transport";
  }

  if (bytesStopped || stopped(deltas.framesSent)) {
    // Without framesEncoded nothing shows that frames were ready to send.
    return deltas.framesEncoded === undefined ? "unknown" : "transport";
  }

  return null;
}

function num(value: unknown): number | undefined {
  return typeof value === "number" && Number.isFinite(value)
    ? value
    : undefined;
}

function sum(a: number | undefined, b: number | undefined) {
  if (a === undefined) {
    return b;
  }
  return b === undefined ? a : a + b;
}

function max(a: number | undefined, b: number | undefined) {
  if (a === undefined) {
    return b;
  }
  return b === undefined ? a : Math.max(a, b);
}

// Simulcast produces one outbound-rtp entry per layer: counters are summed,
// frame size and fps are taken from the largest layer.
export function senderVideoSample(
  stats: ConferenceApiStats
): SenderVideoSample | null {
  const sample: SenderVideoSample = { ts: stats.ts, outboundCount: 0 };

  for (const entry of Object.values(stats.stats || {})) {
    const report = entry as unknown as Record<string, unknown>;
    const kind = report["kind"] ?? report["mediaType"];
    if (kind !== "video") {
      continue;
    }

    if (report["type"] === "outbound-rtp") {
      sample.outboundCount++;
      sample.framesEncoded = sum(
        sample.framesEncoded,
        num(report["framesEncoded"])
      );
      sample.framesSent = sum(sample.framesSent, num(report["framesSent"]));
      sample.bytesSent = sum(sample.bytesSent, num(report["bytesSent"]));
      sample.packetsSent = sum(sample.packetsSent, num(report["packetsSent"]));
      sample.frameWidth = max(sample.frameWidth, num(report["frameWidth"]));
      sample.frameHeight = max(sample.frameHeight, num(report["frameHeight"]));
      sample.framesPerSecond = max(
        sample.framesPerSecond,
        num(report["framesPerSecond"])
      );
      const reason = report["qualityLimitationReason"];
      if (typeof reason === "string" && reason !== "none") {
        sample.qualityLimitationReason = reason;
      }
    } else if (report["type"] === "media-source") {
      sample.sourceFrames = max(sample.sourceFrames, num(report["frames"]));
      sample.sourceFramesPerSecond = max(
        sample.sourceFramesPerSecond,
        num(report["framesPerSecond"])
      );
      sample.sourceWidth = max(sample.sourceWidth, num(report["width"]));
      sample.sourceHeight = max(sample.sourceHeight, num(report["height"]));
    } else if (report["type"] === "remote-inbound-rtp") {
      sample.remoteRoundTripTime = max(
        sample.remoteRoundTripTime,
        num(report["roundTripTime"])
      );
      sample.remoteFractionLost = max(
        sample.remoteFractionLost,
        num(report["fractionLost"])
      );
    }
  }

  return sample.outboundCount > 0 ? sample : null;
}

const MAX_REPORTED_SCREEN_SHARES = 4;

// Report Problem: snapshots of the local screen shares. Never throws, so a
// diagnostics failure cannot break the report.
export function screenShareDiagnosticsSnapshots(
  streams: readonly unknown[] | null | undefined
): unknown[] {
  const snapshots: unknown[] = [];

  for (const stream of streams ?? []) {
    if (snapshots.length >= MAX_REPORTED_SCREEN_SHARES) {
      break;
    }
    const snapshot = (stream as { diagnosticsSnapshot?: unknown } | null)
      ?.diagnosticsSnapshot;
    if (typeof snapshot !== "function") {
      continue;
    }
    try {
      snapshots.push(snapshot.call(stream));
    } catch {
      snapshots.push({ snapshotFailed: true });
    }
  }

  return snapshots;
}

export class ScreenShareDiagnostics {
  private readonly options: ScreenShareDiagnosticsOptions;

  private track: MediaStreamTrack | null = null;
  private detachTrack: (() => void) | null = null;
  private lastEnabled: boolean | null = null;

  private lastStatsTs: number | null = null;
  private prevSample: SenderVideoSample | null = null;
  private assessedSamples = 0;
  private samplesSinceSummary = 0;

  private stalledStreak = 0;
  private healthyStreak = 0;
  private stallSince: number | null = null;
  private stallEpisodes = 0;
  // Cause of the reported stall, and of the last assessed sample.
  private stallCause: ScreenShareStallCause | null = null;
  private lastCause: ScreenShareStallCause | null = null;
  // Whether the last sample could be judged at all (live track, counters),
  // and when the last judged sample was taken (stallMs only grows with it).
  private lastMeasured = false;
  private lastAssessedAt: number | null = null;

  private readonly recentEvents: Record<string, unknown>[] = [];
  private readonly recentSamples: Record<string, unknown>[] = [];

  private trackEventLogs = 0;
  private trackEventsSuppressed = false;
  private mutes = 0;
  private unmutes = 0;
  private ended = false;

  private publishStartedAt: number | null = null;
  private publishedAt: number | null = null;
  private stopped = false;

  constructor(
    private readonly streamId: string,
    private readonly logger: (...args: unknown[]) => void,
    options: Partial<ScreenShareDiagnosticsOptions> = {}
  ) {
    this.options = { ...SCREEN_SHARE_DIAGNOSTICS_DEFAULTS, ...options };
  }

  get stallSuspected() {
    return this.stallSince !== null;
  }

  // Bounded, sanitized state for Report Problem: no label, deviceId or pixels.
  snapshot() {
    try {
      return {
        streamId: this.streamId,
        takenAt: Date.now(),
        assessment: this.assessment(),
        contentInspected: false,
        stallCause: this.stallCause,
        lastSampleCause: this.lastCause,
        stalledSamples: this.stalledStreak,
        stallMs:
          this.stallSince === null || this.lastAssessedAt === null
            ? null
            : this.lastAssessedAt - this.stallSince,
        publishedMs: this.since(this.publishedAt),
        stopped: this.stopped,
        ...this.counters(),
        track: this.trackState(),
        settings: this.track ? screenTrackSettingsSummary(this.track) : null,
        lastSample: this.prevSample ? this.sampleFields(this.prevSample) : null,
        recentSamples: [...this.recentSamples],
        recentEvents: [...this.recentEvents]
      };
    } catch {
      return { streamId: this.streamId, snapshotFailed: true };
    }
  }

  private assessment(): ScreenShareAssessment {
    const track = this.track;
    if (this.ended || (track && (track.readyState !== "live" || track.muted))) {
      return "track-ended-or-muted";
    }
    if (this.stopped || !track?.enabled) {
      // Stopped, detached or intentionally disabled: nothing to judge.
      return "insufficient-evidence";
    }
    if (this.stallSince !== null) {
      switch (this.stallCause) {
        case "transport":
          return "transport-stall-suspected";
        case "capture":
        case "encoder":
        case "capture-or-encoder":
          return "capture-or-encoder-stall-suspected";
        default:
          return "stall-suspected-cause-unclear";
      }
    }
    if (this.lastMeasured && this.lastCause === null) {
      return "counters-healthy-content-unverified";
    }
    // No assessed sample yet, or a stall below the threshold.
    return "insufficient-evidence";
  }

  publishStart() {
    this.guard(() => {
      this.publishStartedAt = Date.now();
      this.log(SCREEN_SHARE_EVENTS.publishStart, {});
    });
  }

  publishSuccess() {
    this.guard(() => {
      this.publishedAt = Date.now();
      this.log(SCREEN_SHARE_EVENTS.publishSuccess, {
        durationMs: this.since(this.publishStartedAt),
        track: this.trackState()
      });
    });
  }

  publishFailure(error: unknown) {
    this.guard(() =>
      this.log(SCREEN_SHARE_EVENTS.publishFailure, {
        durationMs: this.since(this.publishStartedAt),
        ...errorLogSummary(error),
        track: this.trackState()
      })
    );
  }

  // Idempotent: logs the stop once and releases the track listeners.
  stop() {
    this.guard(() => {
      if (this.stopped) {
        return;
      }
      this.stopped = true;

      const fields = {
        published: this.publishedAt !== null,
        publishedMs: this.since(this.publishedAt),
        ...this.counters(),
        track: this.trackState()
      };
      this.setTrack(null);
      this.log(SCREEN_SHARE_EVENTS.publishStop, fields);
    });
  }

  attachTrack(track: MediaStreamTrack | null) {
    this.guard(() => {
      if (this.stopped || track === this.track) {
        return;
      }

      this.setTrack(track);

      if (track) {
        this.log(SCREEN_SHARE_EVENTS.trackAttached, {
          track: trackLogSummary(track),
          settings: screenTrackSettingsSummary(track)
        });
      }
    });
  }

  onStats(stats: ConferenceApiStats | null | undefined) {
    this.guard(() => {
      if (this.stopped || !stats || stats.ts === this.lastStatsTs) {
        return;
      }
      this.lastStatsTs = stats.ts;

      this.checkEnabled();

      const sample = senderVideoSample(stats);
      if (!sample) {
        return;
      }

      const prev = this.prevSample;
      this.prevSample = sample;
      if (!prev) {
        return;
      }

      const deltas = this.deltas(sample, prev);
      if (!deltas) {
        // Counters went backwards (new producer, replaced track): rebaseline.
        this.lastCause = null;
        this.lastMeasured = false;
        this.resetStreaks();
        return;
      }

      this.assessedSamples++;
      this.assessStall(sample, deltas);
      this.pushBounded(
        this.recentSamples,
        {
          ts: sample.ts,
          cause: this.lastCause,
          deltas,
          ...this.sampleFields(sample)
        },
        this.options.historySamples
      );
      this.maybeSummary(sample, deltas);
    });
  }

  private assessStall(sample: SenderVideoSample, deltas: Deltas) {
    const track = this.track;
    const eligible =
      !!track && track.readyState === "live" && !track.muted && track.enabled;
    const measurable =
      deltas.framesEncoded !== undefined || deltas.bytesSent !== undefined;

    if (!eligible || !measurable) {
      // A muted, disabled or ended track is expected not to progress;
      // a stall is only suspected while the track claims to deliver frames.
      this.lastCause = null;
      this.lastMeasured = false;
      this.resetStreaks();
      return;
    }

    const cause = stallCause(deltas, sample.qualityLimitationReason);
    this.lastCause = cause;
    this.lastMeasured = true;
    this.lastAssessedAt = sample.ts;

    if (cause) {
      this.stalledStreak++;
      this.healthyStreak = 0;

      if (
        this.stallSince === null &&
        this.stalledStreak >= this.options.stallEnterSamples
      ) {
        this.stallSince = sample.ts;
        this.stallCause = cause;
        this.stallEpisodes++;
        this.log(SCREEN_SHARE_EVENTS.stallSuspected, {
          cause,
          stalledSamples: this.stalledStreak,
          deltas,
          sample: this.sampleFields(sample),
          track: this.trackState()
        });
      }
    } else {
      this.healthyStreak++;
      this.stalledStreak = 0;

      if (
        this.stallSince !== null &&
        this.healthyStreak >= this.options.stallClearSamples
      ) {
        const stallMs = sample.ts - this.stallSince;
        const clearedCause = this.stallCause;
        this.stallSince = null;
        this.stallCause = null;
        this.log(SCREEN_SHARE_EVENTS.stallCleared, {
          cause: clearedCause,
          stallMs,
          healthySamples: this.healthyStreak,
          deltas,
          sample: this.sampleFields(sample),
          track: this.trackState()
        });
      }
    }
  }

  private maybeSummary(sample: SenderVideoSample, deltas: Deltas) {
    if (
      this.assessedSamples !== 1 &&
      ++this.samplesSinceSummary < this.options.summaryEverySamples
    ) {
      return;
    }
    this.samplesSinceSummary = 0;

    this.log(SCREEN_SHARE_EVENTS.summary, {
      deltas,
      sample: this.sampleFields(sample),
      stallSuspected: this.stallSuspected,
      ...this.counters(),
      track: this.trackState()
    });
  }

  private deltas(
    curr: SenderVideoSample,
    prev: SenderVideoSample
  ): Deltas | null {
    const deltas: Deltas = {
      intervalMs: curr.ts - prev.ts
    };

    for (const counter of COUNTERS) {
      const a = curr[counter];
      const b = prev[counter];
      if (a === undefined || b === undefined) {
        continue;
      }
      if (a < b) {
        return null;
      }
      deltas[counter] = a - b;
    }

    return deltas;
  }

  private checkEnabled() {
    const track = this.track;
    if (!track || track.enabled === this.lastEnabled) {
      return;
    }
    this.lastEnabled = track.enabled;
    this.trackEvent(SCREEN_SHARE_EVENTS.trackEnabledChanged, track);
  }

  private setTrack(track: MediaStreamTrack | null) {
    this.detachTrack?.();
    this.detachTrack = null;

    this.track = track;
    this.lastEnabled = track ? track.enabled : null;
    this.prevSample = null;
    this.lastCause = null;
    this.lastMeasured = false;
    this.resetStreaks();

    if (!track || typeof track.addEventListener !== "function") {
      return;
    }

    const onMute = () =>
      this.guard(() => {
        this.mutes++;
        this.trackEvent(SCREEN_SHARE_EVENTS.trackMute, track);
      });
    const onUnmute = () =>
      this.guard(() => {
        this.unmutes++;
        this.trackEvent(SCREEN_SHARE_EVENTS.trackUnmute, track);
      });
    const onEnded = () =>
      this.guard(() => {
        if (this.ended) {
          return;
        }
        this.ended = true;
        // Always logged: it happens at most once and ends the share.
        this.log(SCREEN_SHARE_EVENTS.trackEnded, {
          ...this.counters(),
          track: trackLogSummary(track)
        });
      });

    track.addEventListener("mute", onMute);
    track.addEventListener("unmute", onUnmute);
    track.addEventListener("ended", onEnded);

    this.detachTrack = () => {
      track.removeEventListener("mute", onMute);
      track.removeEventListener("unmute", onUnmute);
      track.removeEventListener("ended", onEnded);
    };
  }

  private trackEvent(event: string, track: MediaStreamTrack) {
    if (this.trackEventLogs >= this.options.maxTrackEventLogs) {
      if (!this.trackEventsSuppressed) {
        this.trackEventsSuppressed = true;
        this.log(SCREEN_SHARE_EVENTS.trackEventsSuppressed, {
          maxTrackEventLogs: this.options.maxTrackEventLogs,
          ...this.counters()
        });
      }
      return;
    }

    this.trackEventLogs++;
    this.log(event, {
      track: trackLogSummary(track),
      stallSuspected: this.stallSuspected
    });
  }

  private resetStreaks() {
    this.stalledStreak = 0;
    this.healthyStreak = 0;
  }

  private sampleFields(sample: SenderVideoSample) {
    const { ts: _ts, ...fields } = sample;
    return fields;
  }

  private counters() {
    return {
      samples: this.assessedSamples,
      stallEpisodes: this.stallEpisodes,
      stallActive: this.stallSuspected,
      mutes: this.mutes,
      unmutes: this.unmutes,
      ended: this.ended
    };
  }

  private trackState() {
    return this.track ? trackLogSummary(this.track) : null;
  }

  private since(ts: number | null) {
    return ts === null ? null : Date.now() - ts;
  }

  private log(event: string, fields: Record<string, unknown>) {
    const sinceStartMs = this.since(this.publishStartedAt);
    this.pushBounded(
      this.recentEvents,
      { at: Date.now(), event, sinceStartMs, ...fields },
      this.options.historyEvents
    );

    // A failing logger must not leave the state machine half-updated.
    this.guard(() =>
      this.logger(event, {
        streamId: this.streamId,
        sinceStartMs,
        ...fields
      })
    );
  }

  private pushBounded<T>(list: T[], item: T, size: number) {
    list.push(item);
    if (list.length > size) {
      list.splice(0, list.length - size);
    }
  }

  // Diagnostics must never break the stream they observe.
  private guard(fn: () => void) {
    try {
      fn();
    } catch {
      // swallowed on purpose
    }
  }
}
